import { Box, Typography } from "@mui/material";
import { KPICollection } from "./types";
import { formatToMillions, roundToDecimalPlaces } from "../../../utils/numbers";

interface KPICardProps {
  title: string;
  value: number | null;
  change: number | null;
  formatter?: (value: number) => string;
  unit?: string;
}

const KPICard = ({ title, value, change, formatter, unit }: KPICardProps) => {
  if (value === null) {
    return null;
  }

  const isPositive = (num: number | null) => num !== null && num >= 0;
  const isChangePositive = change !== null && isPositive(Number(roundToDecimalPlaces(change)));

  const formatValue = (val: number) => {
    if (formatter) {
      return formatter(val);
    }
    return roundToDecimalPlaces(val);
  };

  const displayValue = formatValue(value) + (unit || "");

  return (
    <Box
      sx={{
        width: "18%",
        minWidth: "140px",
        borderRadius: 1,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.paper",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          textAlign: "center",
          width: "100%",
          color: "text.secondary",
          mb: 1,
          fontWeight: 500,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 26,
          textAlign: "center",
          fontWeight: 600,
          width: "100%",
          mb: 0.5,
        }}
      >
        {displayValue}
      </Typography>

      {change !== null && (
        <Typography
          sx={{
            fontSize: 12,
            textAlign: "center",
            width: "100%",
            color: isChangePositive ? "success.main" : "error.main",
            fontWeight: 500,
          }}
        >
          {roundToDecimalPlaces(change) === "0"
            ? "no change"
            : isChangePositive
            ? "+" + roundToDecimalPlaces(change) + "%"
            : "" + roundToDecimalPlaces(change) + "%"}
          {" vs. previous"}
        </Typography>
      )}
    </Box>
  );
};

export const KPICards = ({ kpis }: { kpis: KPICollection }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        pt: 2,
        pb: 3,
        flexWrap: "wrap",
      }}
    >
      {kpis.revenue && (
        <KPICard
          title="Revenue"
          value={kpis.revenue.current}
          change={kpis.revenue.change_pct}
          formatter={formatToMillions}
        />
      )}
      {kpis.units_shipped && (
        <KPICard
          title="Units Shipped"
          value={kpis.units_shipped.current}
          change={kpis.units_shipped.change_pct}
          formatter={(val) => formatToMillions(val)}
        />
      )}
      {kpis.in_stock_rate && (
        <KPICard
          title="In-Stock Rate"
          value={kpis.in_stock_rate.current}
          change={kpis.in_stock_rate.change_pct}
          formatter={(val) => roundToDecimalPlaces(val)}
          unit="%"
        />
      )}
      {kpis.inventory_dollars && (
        <KPICard
          title="Inventory Value"
          value={kpis.inventory_dollars.current}
          change={kpis.inventory_dollars.change_pct}
          formatter={formatToMillions}
        />
      )}
      {kpis.months_of_supply && (
        <KPICard
          title="Months of Supply"
          value={kpis.months_of_supply.current}
          change={kpis.months_of_supply.change_pct}
          formatter={(val) => roundToDecimalPlaces(val)}
        />
      )}
    </Box>
  );
};
